import type { Component } from "vue";

import AntigravityAgentIcon from "~/constants/agents/agy/AgentIcon.vue";
import ClaudeCodeAgentIcon from "~/constants/agents/claude-code-via-replicas/AgentIcon.vue";
import CodexAgentIcon from "~/constants/agents/codex-via-replicas/AgentIcon.vue";
import CodexIcon from "~/constants/agents/codex-via-replicas/Icon.vue";
import CopilotAgentIcon from "~/constants/agents/copilot/AgentIcon.vue";
import CursorAgentIcon from "~/constants/agents/cursor/AgentIcon.vue";
import DevinAgentIcon from "~/constants/agents/devin/AgentIcon.vue";
import DevinIcon from "~/constants/agents/devin/Icon.vue";
import GeminiAgentIcon from "~/constants/agents/gemini/AgentIcon.vue";
import GrokAgentIcon from "~/constants/agents/grok/AgentIcon.vue";
import MuseAgentIcon from "~/constants/agents/muse/AgentIcon.vue";
import OpenCodeAgentIcon from "~/constants/agents/opencode/AgentIcon.vue";
import PrimeIntellectAgentIcon from "~/constants/agents/prime-agent/AgentIcon.vue";
import VibeAgentIcon from "~/constants/agents/vibe/AgentIcon.vue";
import {
  getModelsForProviders,
  MODEL_TO_AVAILABLE_THINKING_LEVELS,
  MODEL_TO_DEFAULT_THINKING_LEVEL,
  PROVIDER_TO_MODELS_ORDERED,
} from "~/constants/model";
import {
  AntigravityIcon,
  ClaudeIcon,
  CopilotIcon,
  CursorIcon,
  GeminiIcon,
  GrokIcon,
  MetaIcon,
  MistralIcon,
  OpenCodeIcon,
  PrimeIntellectIcon,
} from "~/icons";
import { AiModel, AiProvider, AiThinkingLevel, ExecutionMode } from "~/shared/enums";
import type { LocalState } from "~/shared/types";

export const LOCAL_AGENT_KEY = "localAgent";
export const DEFAULT_LOCAL_AGENT = "claude";

export const LOCAL_AGENT_VALUES = [
  "agy",
  "claude",
  "codex",
  "copilot",
  "cursor",
  "devin",
  "gemini",
  "grok",
  "muse",
  "opencode",
  "prime-agent",
  "vibe",
] as const;
export type LocalAgent = (typeof LOCAL_AGENT_VALUES)[number];
const LOCAL_AGENT_VALUE_SET = new Set<string>(LOCAL_AGENT_VALUES);

const NO_MODELS: AiModel[] = [];

const LOCAL_AGENT_MODEL_OPTIONS: Record<LocalAgent, AiModel[]> = {
  claude: PROVIDER_TO_MODELS_ORDERED[AiProvider.ANTHROPIC],
  codex: PROVIDER_TO_MODELS_ORDERED[AiProvider.OPENAI],
  gemini: PROVIDER_TO_MODELS_ORDERED[AiProvider.GOOGLE],
  agy: NO_MODELS,
  opencode: getModelsForProviders([AiProvider.OPENAI, AiProvider.ANTHROPIC, AiProvider.GOOGLE]),
  cursor: NO_MODELS,
  copilot: [AiModel.GPT_6_LUNA, AiModel.GPT_6_ASTRA, AiModel.GEMINI_3_8_FLASH, AiModel.GROK_4_7],
  devin: getModelsForProviders([AiProvider.OPENAI, AiProvider.ANTHROPIC, AiProvider.GOOGLE]),
  "prime-agent": NO_MODELS,
  grok: NO_MODELS,
  muse: PROVIDER_TO_MODELS_ORDERED[AiProvider.META],
  vibe: NO_MODELS,
} as const;

export type LocalAgentDisplay = {
  title: string;
  chipLabel: string;
  icon: Component;
  avatarIcon: Component;
  loginCommand: string;
  loginSessionCommand?: string;
};

type LocalAgentConfigSource =
  | {
      executionMode?: ExecutionMode | null;
      config?: Record<string, unknown> | null;
    }
  | null
  | undefined;

export type LocalAgentModelSettings = {
  model: AiModel;
  thinkingLevel: AiThinkingLevel;
  modelOptions: AiModel[];
  thinkingLevelOptions: AiThinkingLevel[];
};

export const LOCAL_AGENT_DISPLAY = {
  claude: {
    title: "Claude Code",
    chipLabel: "Local Claude",
    loginCommand: "claude auth login",
    icon: ClaudeIcon,
    avatarIcon: ClaudeCodeAgentIcon,
  },
  codex: {
    title: "Codex",
    chipLabel: "Local Codex",
    loginCommand: "codex login",
    icon: CodexIcon,
    avatarIcon: CodexAgentIcon,
  },
  copilot: {
    title: "Copilot",
    chipLabel: "Local Copilot",
    loginCommand: "copilot login",
    icon: CopilotIcon,
    avatarIcon: CopilotAgentIcon,
  },
  cursor: {
    title: "Cursor",
    chipLabel: "Local Cursor",
    loginCommand: "cursor-agent login",
    icon: CursorIcon,
    avatarIcon: CursorAgentIcon,
  },
  devin: {
    title: "Devin",
    chipLabel: "Local Devin",
    loginCommand: "devin auth login",
    icon: DevinIcon,
    avatarIcon: DevinAgentIcon,
  },
  gemini: {
    title: "Gemini",
    chipLabel: "Local Gemini",
    icon: GeminiIcon,
    avatarIcon: GeminiAgentIcon,
    loginCommand: "gemini",
  },
  agy: {
    title: "Antigravity",
    chipLabel: "Local Antigravity",
    loginCommand: "agy",
    icon: AntigravityIcon,
    avatarIcon: AntigravityAgentIcon,
  },
  grok: {
    title: "Grok",
    chipLabel: "Local Grok",
    icon: GrokIcon,
    avatarIcon: GrokAgentIcon,
    loginCommand: "grok login",
  },
  muse: {
    title: "Muse",
    chipLabel: "Local Muse",
    icon: MetaIcon,
    avatarIcon: MuseAgentIcon,
    loginCommand: "muse login",
  },
  opencode: {
    title: "OpenCode",
    chipLabel: "Local OpenCode",
    icon: OpenCodeIcon,
    avatarIcon: OpenCodeAgentIcon,
    loginCommand: "opencode auth login",
  },
  "prime-agent": {
    title: "Prime Agent",
    chipLabel: "Local Prime Agent",
    icon: PrimeIntellectIcon,
    avatarIcon: PrimeIntellectAgentIcon,
    loginCommand: "prime-agent",
    loginSessionCommand: "/login",
  },
  vibe: {
    title: "Vibe",
    chipLabel: "Local Vibe",
    icon: MistralIcon,
    avatarIcon: VibeAgentIcon,
    loginCommand: "vibe --setup",
  },
} as const satisfies Record<LocalAgent, LocalAgentDisplay>;

export const LOCAL_AGENT_OPTIONS = LOCAL_AGENT_VALUES.map((value) => ({ value, ...LOCAL_AGENT_DISPLAY[value] }));

export const isLocalAgent = (value: unknown): value is LocalAgent =>
  typeof value === "string" && LOCAL_AGENT_VALUE_SET.has(value);

export const getLocalAgent = (value: unknown): LocalAgent | null => (isLocalAgent(value) ? value : null);

export const getConfiguredLocalAgent = (agent: LocalAgentConfigSource): LocalAgent | null => {
  if (agent?.executionMode !== ExecutionMode.LOCAL) {
    return null;
  }
  return getLocalAgent(agent.config?.[LOCAL_AGENT_KEY]) ?? DEFAULT_LOCAL_AGENT;
};

export const getLocalAgentDisplay = (value: unknown): LocalAgentDisplay | null => {
  const localAgent = getLocalAgent(value);
  return localAgent ? LOCAL_AGENT_DISPLAY[localAgent] : null;
};

export const getLocalAgentModelSettings = (
  localAgent: LocalAgent,
  model: AiModel,
  thinkingLevel: AiThinkingLevel,
  localState: LocalState | null = null
): LocalAgentModelSettings => {
  const providerModels =
    localAgent === "opencode" && localState !== null && "providers" in localState
      ? getModelsForProviders(localState.providers)
      : [];
  const modelOptions = providerModels.length > 0 ? providerModels : LOCAL_AGENT_MODEL_OPTIONS[localAgent];
  const validModel = modelOptions.includes(model)
    ? model
    : (modelOptions.find((option) => MODEL_TO_AVAILABLE_THINKING_LEVELS.get(option)?.includes(thinkingLevel)) ??
      modelOptions[0] ??
      AiModel.AUTO);
  const thinkingLevelOptions =
    validModel === AiModel.AUTO ? [] : (MODEL_TO_AVAILABLE_THINKING_LEVELS.get(validModel) ?? []);
  const defaultLevel = MODEL_TO_DEFAULT_THINKING_LEVEL[validModel];
  const validThinkingLevel = thinkingLevelOptions.includes(thinkingLevel)
    ? thinkingLevel
    : thinkingLevelOptions.includes(defaultLevel)
      ? defaultLevel
      : (thinkingLevelOptions[0] ?? AiThinkingLevel.NONE);

  return {
    model: validModel,
    thinkingLevel: validThinkingLevel,
    modelOptions,
    thinkingLevelOptions,
  };
};
